'use client';

import { useState, useEffect, useRef, type ReactNode } from 'react';
import type { StaticImageData } from 'next/image';
import { usePathname } from 'next/navigation';
import { AnimatePresence, motion } from 'framer-motion';
import flags from 'react-phone-number-input/flags';
import Image from '@/helpers/Image';
import { cn } from '@/lib/utils';
import { NON_DEFAULT_LANGUAGES, DEFAULT_LANGUAGE } from '@/sanity/globals/I18n';
import payoutFresco from '@/public/home/payout-popup/p-fresco.jpg';
import payoutStatue from '@/public/home/payout-popup/p-statue.png';
import repeatFresco from '@/public/home/payout-popup/r-fresco.jpg';
import repeatStatue from '@/public/home/payout-popup/r-statue.png';
import challengeFresco from '@/public/home/payout-popup/c-fresco.jpg';
import challengeStatue from '@/public/home/payout-popup/c-statue.png';
import fundedFresco from '@/public/home/payout-popup/f-fresco.jpg';
import fundedStatue from '@/public/home/payout-popup/f-statue.png';
import shieldIcon from '@/public/home/payout-popup/shield.svg';
import closeIcon from '@/public/home/payout-popup/close.svg';

const FEED_URL = '/iqc_popup_social_proof_100.json';

type FeedLanguage = 'en' | 'de' | 'pt';

type EventType = 'P' | 'R' | 'C' | 'F';

interface SocialProofItem {
  id: number;
  type: EventType;
  country_code: string;
  account_size: string;
  amount_usd: number | null;
  payout_no: number | null;
  text: Record<FeedLanguage, string>;
  time_label: Record<FeedLanguage, string>;
}

interface EventStyle {
  label: Record<FeedLanguage, string>;
  fresco: StaticImageData;
  statue: StaticImageData;
  // Where the statue sits inside the image area, and how the image is cropped inside that box.
  statueBox: string;
  statueCrop: string;
  chip: string;
  // Glass badge over the image (payout counter or account size); the Payout confirmed card has none.
  badge?: string;
  bar: string;
}

const EVENT_STYLE: Record<EventType, EventStyle> = {
  P: {
    label: { en: 'PAYOUT CONFIRMED', de: 'AUSZAHLUNG BESTÄTIGT', pt: 'PAGAMENTO CONFIRMADO' },
    fresco: payoutFresco,
    statue: payoutStatue,
    statueBox: 'left-[calc(50%+2.5625rem)] top-[0.875rem] h-[15.375rem] w-[26.75rem] -translate-x-1/2',
    statueCrop: 'inset-0',
    chip: 'bg-[#e6edeb] text-[#1b7a60]',
    bar: 'bg-[#228e71]',
  },
  R: {
    label: { en: 'REPEAT PAYOUT', de: 'ERNEUTE AUSZAHLUNG', pt: 'PAGAMENTO RECORRENTE' },
    fresco: repeatFresco,
    statue: repeatStatue,
    statueBox: 'left-[-7.125rem] top-[0.5rem] h-[22.375rem] w-[29.8135rem] blur-[0.5275px]',
    statueCrop: 'inset-0',
    chip: 'bg-[#e6edeb] text-[#1b7a60]',
    bar: 'bg-[#228e71]',
    badge:
      'left-[calc(50%+0.125rem)] h-[4.25rem] rounded-[0.3959rem] border-[1.583px] px-[1.5834rem] leading-[2.375rem] backdrop-blur-[2.375px]',
  },
  C: {
    label: { en: 'CHALLENGE PASSED', de: 'CHALLENGE BESTANDEN', pt: 'CHALLENGE APROVADO' },
    fresco: challengeFresco,
    statue: challengeStatue,
    statueBox:
      'bottom-[-0.75rem] left-[calc(50%+0.2588rem)] h-[16.375rem] w-[16.6513rem] -translate-x-1/2 overflow-hidden',
    statueCrop: 'inset-y-0 left-[-15.84%] w-[131.71%]',
    chip: 'bg-[#003828] text-white',
    bar: 'bg-[#003828]',
    badge:
      'left-[calc(50%+0.0625rem)] rounded-[0.25rem] border px-[1rem] py-[0.5rem] leading-[1.875rem] backdrop-blur-[1.5px]',
  },
  F: {
    label: { en: 'FUNDED ACCOUNT', de: 'FUNDED ACCOUNT', pt: 'CONTA FUNDED' },
    fresco: fundedFresco,
    statue: fundedStatue,
    statueBox:
      'left-[calc(50%+0.5rem)] top-[0.25rem] h-[17.125rem] w-[14.125rem] -translate-x-1/2 overflow-hidden',
    statueCrop: 'left-[-0.03%] top-[-2.61%] h-[102.61%] w-[100.06%]',
    chip: 'bg-[#e2f3ef] text-[#0b5b45]',
    bar: 'bg-[#5e7a72]',
    badge:
      'left-[calc(50%+0.0625rem)] rounded-[0.25rem] border px-[1rem] pb-[0.5rem] pt-[1rem] leading-[2.375rem] backdrop-blur-[1.5px]',
  },
};

const VERIFIED_NOTE: Record<FeedLanguage, string> = {
  en: 'Verified · Name changed',
  de: 'Verifiziert · Name geändert',
  pt: 'Verificado · Nome alterado',
};

// The feed keys Portuguese as `pt`; the site's locale for it is `br`.
const FEED_LANGUAGE: Record<string, FeedLanguage> = {
  en: 'en',
  de: 'de',
  br: 'pt',
};

function shuffle<T>(items: T[]): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// The feed writes amounts as `$1,490` in English and `$1.490` in German and Portuguese.
function formatAmount(amount: number, language: FeedLanguage) {
  return `$${amount.toLocaleString(language === 'en' ? 'en-US' : 'de-DE')}`;
}

// Wraps the amount (or the account size) inside the feed sentence in the highlight style.
function highlight(text: string, value: string): ReactNode {
  const at = text.indexOf(value);
  if (at === -1) return text;
  return (
    <>
      {text.slice(0, at)}
      <span className="font-blacker text-[2.5rem] font-normal italic leading-[3rem] tracking-[-0.025rem] text-[#003828]">
        {value}
      </span>
      {text.slice(at + value.length)}
    </>
  );
}

const SHOW_MS = 4500;
const PAUSE_MS = 10000;
const INITIAL_DELAY_MS = 5000;

export default function PayoutToast() {
  const [visible, setVisible] = useState(false);
  const [index, setIndex] = useState(0);
  const [items, setItems] = useState<SocialProofItem[]>([]);
  // Hides the current card ahead of time; set by the rotation effect.
  const dismissRef = useRef<() => void>(() => {});

  const pathname = usePathname();
  const language =
    NON_DEFAULT_LANGUAGES.find(
      (l) => pathname.startsWith(`/${l}/`) || pathname === `/${l}`,
    ) ?? DEFAULT_LANGUAGE;

  const feedLanguage = FEED_LANGUAGE[language] ?? 'en';

  useEffect(() => {
    let cancelled = false;
    fetch(FEED_URL)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data: { items?: SocialProofItem[] }) => {
        if (!cancelled && data.items?.length) setItems(shuffle(data.items));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!items.length) return;

    let t1: ReturnType<typeof setTimeout>;
    let t2: ReturnType<typeof setTimeout>;
    let msgIndex = 0;
    let isShown = false;

    // Closing a card takes the same path as the timeout: the rotation carries on after the pause.
    const hide = () => {
      if (!isShown) return;
      isShown = false;
      clearTimeout(t1);
      setVisible(false);
      msgIndex = (msgIndex + 1) % items.length;
      t2 = setTimeout(cycle, PAUSE_MS);
    };

    const cycle = () => {
      setIndex(msgIndex);
      setVisible(true);
      isShown = true;
      t1 = setTimeout(hide, SHOW_MS);
    };

    dismissRef.current = hide;
    t2 = setTimeout(cycle, INITIAL_DELAY_MS);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [items.length]);

  const item = items[index];
  const style = item ? EVENT_STYLE[item.type] : undefined;
  const Flag = item ? flags[item.country_code as keyof typeof flags] : undefined;
  const heroValue = item
    ? item.amount_usd !== null
      ? formatAmount(item.amount_usd, feedLanguage)
      : item.account_size
    : '';
  const badgeText = item?.type === 'R' ? `#${item.payout_no}` : item?.account_size;

  return (
    <div className="pointer-events-none fixed bottom-[3rem] left-[3rem] z-50 hidden lg:block">
      <AnimatePresence>
        {visible && item && style && (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ type: 'spring', stiffness: 120, damping: 18 }}
            className="pointer-events-auto relative h-[16.5rem] w-[57.5rem]"
          >
            <div className="flex h-full w-full overflow-clip rounded-[0.25rem] border border-[rgba(0,0,0,0.1)] bg-white shadow-[0_1.5rem_3rem_-2rem_rgba(0,27,20,0.22)]">
              <div className="relative h-full w-[15.5rem] shrink-0 overflow-clip border-r border-[rgba(0,0,0,0.1)] bg-[#04271d]">
                <div className="absolute inset-y-0 left-[-0.5rem] right-[-0.625rem]">
                  <Image
                    src={style.fresco}
                    alt=""
                    fill
                    sizes="8vw"
                    className="object-cover"
                    style={{ width: '100%', height: '100%' }}
                  />
                </div>
                <div className={cn('absolute', style.statueBox)}>
                  <div className={cn('absolute', style.statueCrop)}>
                    <Image
                      src={style.statue}
                      alt=""
                      fill
                      sizes="14vw"
                      className="object-cover"
                      style={{ width: '100%', height: '100%' }}
                    />
                  </div>
                </div>
                {style.badge && (
                  <div
                    className={cn(
                      'absolute bottom-[1rem] flex -translate-x-1/2 items-center overflow-clip border-[rgba(255,255,255,0.1)] bg-[#2c3e39]',
                      style.badge,
                    )}
                  >
                    <span className="whitespace-nowrap font-blacker text-[2.5rem] font-normal italic tracking-[-0.025rem] text-white">
                      {badgeText}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-[0.75rem] overflow-clip py-[1.75rem] pl-[2rem] pr-[2.5rem]">
                <div className="flex items-center gap-[1rem] overflow-clip">
                  {Flag && (
                    <span className="flex h-[1.875rem] w-[2.625rem] shrink-0 overflow-hidden [&_svg]:!h-full [&_svg]:!w-full">
                      <Flag title={item.country_code} />
                    </span>
                  )}
                  <span
                    className={cn(
                      'shrink-0 whitespace-nowrap rounded-[0.25rem] px-[1rem] py-[0.25rem] font-geist text-[1.75rem] font-semibold leading-[2.5rem] tracking-[0.07rem]',
                      style.chip,
                    )}
                  >
                    {style.label[feedLanguage]}
                  </span>
                  <span className="shrink-0 whitespace-nowrap font-geist text-[1.75rem] font-normal leading-[2.5rem] text-[rgba(0,0,0,0.45)]">
                    {item.time_label[feedLanguage]}
                  </span>
                </div>
                <p className="mb-0 font-geist text-[2rem] font-medium leading-[3rem] tracking-[-0.04rem] text-[rgba(0,0,0,0.7)] [text-wrap:balance]">
                  {highlight(item.text[feedLanguage], heroValue)}
                </p>
                <div className="flex items-center gap-[0.5rem]">
                  <Image src={shieldIcon} alt="" width={16} height={16} />
                  <span className="whitespace-nowrap font-geist text-[1.75rem] font-normal leading-[2.5rem] text-[rgba(0,0,0,0.45)]">
                    {VERIFIED_NOTE[feedLanguage]}
                  </span>
                </div>
              </div>
            </div>
            <motion.div
              initial={{ scaleX: 1 }}
              animate={{ scaleX: 0 }}
              transition={{ duration: SHOW_MS / 1000, ease: 'linear' }}
              className={cn('absolute bottom-0 left-0 h-[0.25rem] w-full origin-left', style.bar)}
            />
            <button
              type="button"
              aria-label="Close"
              onClick={() => dismissRef.current()}
              className="absolute left-[55rem] top-[-1.5rem] flex h-[4rem] w-[4rem] items-center justify-center rounded-[0.25rem] border border-[rgba(0,0,0,0.12)] bg-white"
            >
              <Image src={closeIcon} alt="" width={16} height={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
